import * as React from 'react'
import { Button } from '@mobiscroll/react-latest'
import ExpandIcon from '../../icons/ui/ExpandIcon'
import CollapseIcon from '../../icons/ui/CollapseIcon'
import { Link, Route, BrowserRouter, RouteComponentProps } from 'react-router-dom'
import Map from './Map'
import DrawingToolsButtons from './DrawingToolsButtons'
import ServiceZones from './ServiceZones'
import {
  IStationUserGoinZoneObject,
  IStationUserZoneObject,
  StationUserGoinZoneShapeType,
  StationUserGoinZoneType
} from '../../../types'
import GoinZonesListItem from './GoinZonesListItem'
import RemoveZoneModal from './RemoveZoneModal'
import tj from '@mapbox/togeojson'
import { withNotification } from '../../ui/notification-provider'
import StationUserBottomPanel from '../form/StationUserBottomPanel'
import FloatingForm from 'src/components/ui/form/FloatingForm'
import Plus from 'src/components/icons/organizations/Plus'
import UIMessages from 'src/utils/ui-messages'
import Divider from 'src/components/ui/Divider'
import { handleRedirect } from 'src/utils'

interface IGoinZonesPaneState {
  expanded: { [key: string]: boolean }
  enablePriceAddServiceZone: boolean
  adding: boolean
  toAdd: StationUserGoinZoneType
  isSaving: false
  color: string
  showPallete: boolean
  map: any
  specificErrorMsg: string
  drawBy: DrawingMode
  address: string
  buffer: any[]
  toEdit: IStationUserGoinZoneObject | null
  notification?: any
  zones: { [key: string]: IStationUserGoinZoneObject } // temporary placement for zones until this view (create new service) is finished
}

interface IGoinZonesPaneProps extends RouteComponentProps {
  showMessage: (message: string, isError: boolean) => void
  addGoinZone: (goinzone: IStationUserGoinZoneObject, orgId: string, serviceId: string) => Promise<any>
  getGoinzones: (stationUserId: string) => Promise<any>
  removeGoinZone: (zoneId: string, serviceId: string) => Promise<any>
  updateGoinZone: (goinzone: IStationUserGoinZoneObject, serviceId: string) => Promise<any>
  enableButtons: (flag: boolean) => Promise<any>
  orgId: string
  stationUserId: string
  zones: { [key: string]: IStationUserZoneObject }
  viewMode?: boolean
  notification: any
  routeProps: RouteComponentProps
}

const COLOR_PALLETE: string[] = [
  '#D67576',
  '#860A4E',
  '#E44C11',
  '#F8A631',
  '#FFD425',
  '#81761F',
  '#588A33',
  '#16703A',
  '#00599A',
  '#16277C',
  '#643EB5',
  '#C0105B',
  '#FD4D54',
  '#F47817',
  '#FBBE39',
  '#FFE828',
  '#B0B334',
  '#7FB247',
  '#1198A6',
  '#028AD0',
  '#364CA9',
  '#992BAE',
  '#4D342F',
  '#785448',
  '#FFFFFF',
  '#BDBDBD',
  '#757575',
  '#424242',
  '#000000'
]

export enum DrawingMode {
  ZONE = 'zone',
  PLACE = 'place',
  CITY = 'city',
  COUNTY = 'county',
  STATE = 'state',
  KMLFILE = 'kml-file'
}

class StationUserGoinZonePane extends React.Component<IGoinZonesPaneProps, IGoinZonesPaneState> {
  constructor(props: IGoinZonesPaneProps) {
    super(props)
    this.state = {
      isSaving: false,
      adding: false,
      buffer: [],
      zones: {},
      toEdit: null,
      enablePriceAddServiceZone: true,
      specificErrorMsg: '',
      toAdd: StationUserGoinZoneType.SERVICE_ZONE,
      address: '',
      map: null,
      showPallete: false,
      color: '#D67576',
      drawBy: DrawingMode.ZONE,
      expanded: {
        'service zones': true,
        'restricted zones': true,
        'pick up/drop off point': true
      }
    }
  }

  public componentDidMount() {
    this.fetchGoinZones()
  }

  public componentDidCatch() {
    console.log('error catch')
  }

  public fetchGoinZones() {
    return new Promise((approve, reject) => {
      this.props.getGoinzones(this.props.stationUserId).then(() => {
        const zones: { [key: string]: IStationUserGoinZoneObject } = {}
        for (const zoneId of Object.keys(this.props.zones)) {
          if (this.props.zones[zoneId].center) {
            zones[zoneId] = this.convertIZoneToIGoinZone(this.props.zones[zoneId])
          }
        }

        for (const i of Object.keys(zones)) {
          if (zones[i].coordinates) {
            const coors = zones[i].coordinates
            zones[i].coordinates = { lng: coors.lng, lat: coors.lat }
            this.centerTo(zones[i].coordinates)
          }
          zones[i] = { ...zones[i], visible: true }
        }
        this.setState(
          {
            zones
          },
          () => {
            approve()
            this.refreshZones()
          }
        )
      })
    })
  }

  public render() {
    const { stationUserId } = this.props
    const path = `/station-user/${stationUserId}/zones`

    const listStyle: any = {
      pointerEvents: this.state.adding ? 'none' : 'auto',
      opacity: this.state.adding ? 0.4 : 1.0
    }
    const mapInnerStyle: any = {
      zIndex: -1,
      display: 'block',
      width: '100%',
      height: '600px'
    }
    const getRemoveZoneModal = (props: any) => {
      return (
        <div>
          <RemoveZoneModal
            {...props}
            onClose={this.props.history.goBack}
            zones={this.state.zones}
            onRemove={this.onRemoveZone}
          />
        </div>
      )
    }
    return (
      <section className='su-goin-zone-container'>
        <Route path={`${this.props.match.path}/remove-zone/:id`} component={getRemoveZoneModal} />
        <FloatingForm>
          <div className='mbsc-grid no-padding'>
            <div className='mbsc-row'>
              <div className='mbsc-col-12 mbsc-col-lg-4 mbsc-padding'>
                {this.state.adding ? (
                  <>
                    <p className='bold font-grey3'>{UIMessages.stationUser.addServiceZone}</p>
                    <Divider />
                    <div>
                      <DrawingToolsButtons
                        onDrawingToolSelect={this.onDrawingToolSelect}
                        drawBy={this.state.drawBy}
                        map={this.state.map}
                        toAdd={this.state.toAdd}
                        adding={this.state.adding}
                        address={this.state.address}
                        onLocationAutocompleteChange={this.onLocationAutocompleteChange}
                        onLocationAutocompleteDropdownSelect={this.onLocationAutocompleteDropdownSelect}
                        loadKmlFile={this.loadKmlFile}
                      />
                      <ServiceZones
                        color={this.state.color}
                        onCancel={this.onCancel}
                        onClickColorPick={this.onClickColorPick}
                        buffer={this.state.buffer}
                        getColorPallete={this.getColorPallete}
                        showSpecificError={this.showSpecificError}
                        showPallete={this.state.showPallete}
                        specificErrorMsg={this.state.specificErrorMsg}
                        onAddServiceZone={this.onAddServiceZone}
                        showMessage={this.props.showMessage}
                        toEdit={this.state.toEdit}
                      />
                    </div>
                  </>
                ) : (
                  <div style={listStyle}>{this.getLists()}</div>
                )}
              </div>
              <div className='mbsc-col-12 mbsc-col-lg-8 mbsc-padding'>
                <div style={mapInnerStyle}>
                  <Map
                    onAddedShape={this.addBuffer}
                    onMapMounted={this.onMapMounted}
                    enableDrawingManager={this.state.adding && this.state.drawBy === DrawingMode.ZONE}
                    color={this.state.color}
                  />
                </div>
              </div>
            </div>
          </div>
        </FloatingForm>
        {!this.props.viewMode && (
          <StationUserBottomPanel
            onSubmitClick={() => this.handleSaveButtonClick(path)}
            onCancelClick={() => this.handleCancelClick(path)}
          />
        )}
      </section>
    )
  }

  private handleSaveButtonClick = (redirectPath: string) => {
    if (Object.keys(this.props.zones).length > 0) {
      this.props.notification.addNotification({
        title: 'Saved zones!',
        level: 'success'
      })
      handleRedirect(redirectPath)
    } else {
      this.props.notification.addNotification({
        title: 'Please create at least one zone!',
        level: 'error'
      })
    }
  }
  private handleCancelClick = (redirectPath: string) => {
    handleRedirect(redirectPath)
  }
  private convertIZoneToIGoinZone = (zone: IStationUserZoneObject): IStationUserGoinZoneObject => {
    return {
      id: zone.id || '',
      geojson: zone.shape ? zone.shape.coordinates : null,
      coordinates: zone.center
        ? {
            lat: zone.center.coordinates[1],
            lng: zone.center.coordinates[0]
          }
        : {
            lat: 0,
            lng: 0
          },
      radius: zone.radius || 0,
      name: zone.name,
      address: zone.address || '',
      description: zone.description || '',
      price: 0,
      type: zone.shapeType,
      zoneType: zone.type,
      color: zone.color,
      mapObject: null,
      pointType: '',
      visible: false
    }
  }

  private onMapMounted = (map: any) => {
    this.setState({
      map
    })
  }

  private addBuffer = (p: any) => {
    this.clearBuffer()
    const buffer = Object.assign([], this.state.buffer)
    if (p.mapObject.setEditable) {
      p.mapObject.setEditable(true)
    }
    buffer.push(p)
    this.setState({
      buffer
    })
  }

  private centerTo = (center: any, zoom: number = 10) => {
    if (!this.state.map) {
      return
    }
    this.state.map.setCenter(center)
    this.state.map.setZoom(zoom)
  }

  private showSpecificError = (msg: string) => {
    this.props.showMessage(msg, true)
    this.setState({
      specificErrorMsg: msg
    })
  }

  private clearBuffer = () => {
    let buffer = Object.assign([], this.state.buffer)
    for (const i of Object.keys(buffer)) {
      buffer[i].mapObject.setMap(null)
    }
    buffer = []
    this.setState({
      buffer
    })
  }

  private loadKmlFile = (file: any) => {
    const reader = new FileReader()
    const self = this
    self.clearBuffer()
    reader.addEventListener('load', () => {
      const kml = new DOMParser().parseFromString(reader.result + '', 'text/xml')
      const convertedWithStyles = tj.kml(kml, { styles: true })

      const centerPoint = {
        lat: 0,
        lng: 0
      }
      if (typeof convertedWithStyles.features[0].geometry.coordinates[0] === 'object') {
        convertedWithStyles.features[0].geometry.coordinates[0].map((p: any) => {
          centerPoint.lat += p[1]
          centerPoint.lng += p[0]
        })

        centerPoint.lat /= convertedWithStyles.features[0].geometry.coordinates[0].length
        centerPoint.lng /= convertedWithStyles.features[0].geometry.coordinates[0].length

        const data = new google.maps.Data()
        data.setStyle({
          strokeOpacity: 0.8,
          strokeWeight: 1,
          fillColor: self.state.color,
          fillOpacity: 0.35
        })
        data.addGeoJson(convertedWithStyles)
        data.setMap(self.state.map)
        self.addBuffer({
          mapObject: data,
          innerType: StationUserGoinZoneShapeType.SHAPE_MULTIPOLYGON,
          center: { lat: Number(centerPoint.lat), lng: Number(centerPoint.lng) }
        })
        self.state.map.setCenter({
          lat: Number(centerPoint.lat),
          lng: Number(centerPoint.lng)
        })
      } else {
        this.props.notification.addNotification({
          title: 'KML file is not valid please try with some other file',
          level: 'error'
        })
      }
    })
    reader.readAsText(file)
  }

  private fetchPolygon = (q: string) => {
    return new Promise((resolve, reject) => {
      fetch(encodeURI('https://nominatim.openstreetmap.org/search?q=' + q + '&polygon_geojson=1&format=json'))
        .then(response => {
          resolve(response.json())
        })
        .catch(result => {
          console.log('Failed to fetch polygon from openstreetmap: ' + result)
          this.props.showMessage('Failed to fetch polygon from openstreetmap: ' + result, true)
          reject(result)
        })
    })
  }

  private onLocationAutocompleteChange = (address: any) => {
    this.setState({ address })
  }

  private onAddPoint = (center: any, name: string, pointType: string) => {
    this.clearBuffer()
    const marker = new google.maps.Marker({
      position: center,
      map: this.state.map,
      title: name,
      icon: this.getPinSymbol(pointType === 'pickup' ? '#37c47e' : '#ff5c47')
    })
    this.addBuffer({
      mapObject: marker,
      innerType: StationUserGoinZoneShapeType.SHAPE_POINT,
      center: Object.assign({}, center)
    })
  }

  private onLocationAutocompleteDropdownSelect = (address: any) => {
    this.fetchPolygon(address).then(
      (json: any) => {
        this.clearBuffer()
        const first = json[0]
        if (!first || !first.geojson) {
          // TODO: throw error
          console.log("Unable to find '" + address + "'")
          this.props.showMessage("Unable to find '" + address + "'", true)
          return
        }
        this.state.map.setCenter({
          lat: Number(first.lat),
          lng: Number(first.lon)
        })
        const coors = {
          type: 'FeatureCollection',
          features: [
            {
              type: 'Feature',
              geometry: Object.assign({}, first.geojson)
            }
          ]
        }
        const data = new google.maps.Data()
        data.setStyle({
          strokeOpacity: 0.8,
          strokeWeight: 1,
          fillColor: this.state.color,
          fillOpacity: 0.35
        })
        data.addGeoJson(coors)
        data.setMap(this.state.map)
        this.addBuffer({
          mapObject: data,
          innerType: StationUserGoinZoneShapeType.SHAPE_MULTIPOLYGON,
          center: { lat: Number(first.lat), lng: Number(first.lon) }
        })
        this.setState({ address })
      },
      err => {
        // TODO: Handle error calling this service
        return
      }
    )
  }

  private setZoneVisible = (zone: IStationUserGoinZoneObject, visible: boolean) => {
    const zones = Object.assign({}, this.state.zones)

    if (visible) {
      this.centerTo(zones[zone.id].coordinates)
    }

    zones[zone.id].visible = visible
    this.setState(
      {
        zones
      },
      () => {
        this.refreshZones()
      }
    )
  }

  private onEditZone = (zone: IStationUserGoinZoneObject) => {
    const zones = Object.assign({}, this.state.zones)
    Object.keys(zones).map(zid => {
      zones[zid].visible = false
    })
    zones[zone.id].visible = false
    this.addBuffer({
      mapObject: this.buildOnMap(zone, this.state.map, false),
      innerType: zone.type,
      center: Object.assign({}, zone.coordinates)
    })
    this.centerTo(zone.coordinates)
    this.setState(
      {
        toAdd: zone.zoneType,
        adding: true,
        toEdit: zone,
        color: zone.color,
        drawBy: zone.type === StationUserGoinZoneShapeType.SHAPE_MULTIPOLYGON ? DrawingMode.STATE : DrawingMode.ZONE,
        zones
      },
      () => {
        this.refreshZones()
      }
    )
    return new Promise((resolve, reject) => {
      resolve()
    })
  }

  private onDrawingToolSelect = (tool: DrawingMode) => {
    this.setState({
      drawBy: tool,
      address: ''
    })
  }

  private onClickColorPick = () => {
    this.setState({
      showPallete: true
    })
  }

  private getColorPallete = () => {
    const onColorChoose = (color: string) => {
      this.setState({
        showPallete: false,
        color
      })
      for (const c of Object.keys(this.state.buffer)) {
        const buff: any = this.state.buffer[c]
        if (buff.setStyle) {
          buff.setStyle({
            strokeOpacity: 0.8,
            strokeWeight: 1,
            fillColor: color,
            fillOpacity: 0.35
          })
        }
      }
    }
    const colors: any = []
    for (const i of Object.keys(COLOR_PALLETE)) {
      const onClick = () => {
        onColorChoose(COLOR_PALLETE[i])
      }
      const style = {
        margin: '1px',
        display: 'inline-block',
        width: '24px',
        height: '24px',
        backgroundColor: COLOR_PALLETE[i],
        cursor: 'pointer'
      }
      colors.push(
        <div onClick={onClick} key={i} className='rounded' style={style}>
          &nbsp;
        </div>
      )
    }
    return (
      <div style={{ maxWidth: '286px' }} className='block mt1'>
        {colors}
      </div>
    )
  }

  private onCancel = () => {
    this.props.showMessage('', false)
    this.props.enableButtons(false)
    const zones = Object.assign({}, this.state.zones)
    if (this.state.toEdit != null) {
      zones[this.state.toEdit.id].visible = true
    }
    Object.keys(zones).forEach(zid => {
      zones[zid].visible = true
    })
    this.clearBuffer()
    this.setState(
      {
        drawBy: DrawingMode.ZONE,
        toAdd: StationUserGoinZoneType.SERVICE_ZONE,
        adding: false,
        toEdit: null,
        zones
      },
      () => {
        this.refreshZones()
      }
    )
  }

  private onAddServiceZoneAddButton = () => {
    this.props.enableButtons(true)
    const zones = Object.assign({}, this.state.zones)
    Object.keys(zones).forEach(zid => {
      zones[zid].visible = false
    })
    this.clearBuffer()
    this.clearMap()
    this.setState(
      {
        zones,
        adding: true
      },
      () => {
        this.props.showMessage('', false)
      }
    )
  }

  private clearMap = () => {
    for (const i of Object.keys(this.state.zones)) {
      this.state.zones[i].visible = false
      if (this.state.zones[i].mapObject) {
        this.state.zones[i].mapObject.setMap(null)
      }
    }
  }

  private onAddServiceZone = (values: any) => {
    console.log('values - 1: ', values)
    this.props.showMessage('', false)

    return new Promise((resolve, reject) => {
      const proxy: any = this.state.buffer[0]
      // multipolygon
      if (this.state.drawBy !== DrawingMode.ZONE) {
        proxy.mapObject.toGeoJson((feature: any) => {
          const id = values.original ? values.original.id : Math.round(Math.random() * 1500)
          const zone: IStationUserGoinZoneObject = {
            id,
            name: values.zoneTitle,
            color: this.state.color,
            description: values.description,
            price: values.price,
            address: '',
            radius: 0,
            coordinates: {
              lng: proxy.center.lng,
              lat: proxy.center.lat
            },
            visible: false,
            type: feature.features[0].geometry.type,
            zoneType: values.zoneType,
            geojson: feature.features[0].geometry.coordinates,
            mapObject: null,
            pointType: ''
          }
          proxy.mapObject.setMap(null)
          const zones = Object.assign({}, this.state.zones)
          Object.keys(zones).map(zid => {
            zones[zid].visible = false
          })
          const method = values.original ? this.updateGoinZone : this.addGoinZone // update or create new one?
          method(zone).then(() => {
            zones[id] = zone
            this.setState(
              {
                adding: false,
                zones,
                toEdit: null
              },
              () => {
                this.clearBuffer()
                this.refreshZones()
                resolve()
                this.props.showMessage('Saved Service zone succesfully', false)
                this.props.enableButtons(false)
              }
            )
          })
        })
        // circle or drawn polygon
      } else {
        let geojson: any = null
        const center = {
          lat: 0,
          lng: 0
        }
        if (proxy.innerType === StationUserGoinZoneShapeType.SHAPE_POLYGON) {
          geojson = []
          let count = 0
          for (const point of proxy.mapObject.getPath().getArray()) {
            geojson.push([point.lng(), point.lat()])
            center.lat += point.lat()
            center.lng += point.lng()
            count++
          }
          geojson.push(geojson[0])
          center.lat /= count
          center.lng /= count
          geojson = [geojson]
        }

        const id = values.original ? values.original.id : Math.round(Math.random() * 1500)
        const zone: IStationUserGoinZoneObject = {
          id,
          name: values.zoneTitle,
          color: this.state.color,
          description: values.description,
          // zoneType: StationUserGoinZoneType.SERVICE_ZONE,
          zoneType: values.zoneType,
          price: values.price,
          address: '',
          visible: false,
          coordinates:
            proxy.innerType === StationUserGoinZoneShapeType.SHAPE_CIRCLE
              ? {
                  lng: proxy.mapObject.getCenter().lng(),
                  lat: proxy.mapObject.getCenter().lat()
                }
              : center,
          radius: proxy.innerType === StationUserGoinZoneShapeType.SHAPE_CIRCLE ? proxy.mapObject.getRadius() : 0,
          type: proxy.innerType,
          geojson,
          mapObject: null,
          pointType: ''
        }
        proxy.mapObject.setMap(null)
        const zones = Object.assign({}, this.state.zones)
        const method = values.original ? this.updateGoinZone : this.addGoinZone // update or create new one?
        Object.keys(zones).map(zid => {
          zones[zid].visible = false
        })
        method(zone).then(() => {
          zones[id] = zone
          this.setState(
            {
              adding: false,
              zones,
              toEdit: null
            },
            () => {
              this.clearBuffer()
              this.refreshZones()
              resolve()
              this.props.showMessage('Saved Service zone succesfully', false)
            }
          )
        })
      }
      return
    })
  }

  private updateGoinZone = (goinzone: IStationUserGoinZoneObject) => {
    this.clearMap()
    return this.props.updateGoinZone(goinzone, this.props.stationUserId).then(() => {
      this.fetchGoinZones()
    })
  }

  private addGoinZone = (goinzone: IStationUserGoinZoneObject) => {
    this.clearMap()
    return this.props.addGoinZone(goinzone, this.props.orgId, this.props.stationUserId).then(() => {
      this.fetchGoinZones()
    })
  }

  private onRemoveZone = (zone: IStationUserGoinZoneObject) => {
    this.clearMap()
    return new Promise((approve, reject) => {
      this.props.removeGoinZone(zone.id, this.props.stationUserId).then(() => {
        this.fetchGoinZones()
        approve()
      })
    })
  }

  private getPinSymbol = (color: string) => {
    return {
      path: 'M 0,0 C -2,-20 -10,-22 -10,-30 A 10,10 0 1,1 10,-30 C 10,-22 2,-20 0,0 z',
      fillColor: color,
      fillOpacity: 1,
      strokeColor: '#000',
      strokeWeight: 2,
      scale: 1
    }
  }

  private buildOnMap = (zone: IStationUserGoinZoneObject, map: any, card: boolean = false) => {
    const me: any = this
    const buildCard = (zone: IStationUserGoinZoneObject) => {
      return (
        '<div id="content">' +
        '<div id="siteNotice">' +
        '</div>' +
        '<h1 id="firstHeading" class="firstHeading"><span style="display:block; width:16px; height:16px; backgroundColor:\'' +
        zone.color +
        '\';" />' +
        zone.name +
        '</h1>' +
        '<h3 id="firstHeading" class="firstHeading">' +
        zone.zoneType +
        '</h3>' +
        '<div id="bodyContent">' +
        '<p>' +
        zone.description +
        '</p>' +
        '</div>' +
        '</div>'
      )
    }

    const toMap: any | null = (zone: IStationUserGoinZoneObject) => {
      if (zone.type === StationUserGoinZoneShapeType.SHAPE_POINT) {
        const data = new google.maps.Marker({
          position: zone.coordinates,
          map,
          title: zone.name,
          icon: this.getPinSymbol(zone.pointType === 'pickup' ? '#37c47e' : '#ff5c47')
        })

        if (card) {
          const infowindow = new google.maps.InfoWindow({
            content: buildCard(zone),
            maxWidth: 200
          })

          data.addListener('click', () => {
            map.setCenter(data.getPosition())
            infowindow.open(map, data)
          })
        }

        return data
      }
      if (zone.type === StationUserGoinZoneShapeType.SHAPE_MULTIPOLYGON) {
        const coors = {
          type: 'FeatureCollection',
          features: [
            {
              type: 'Feature',
              geometry: {
                type: 'MultiPolygon',
                coordinates: zone.geojson
              }
            }
          ]
        }
        const data = new google.maps.Data()
        data.setStyle({
          strokeOpacity: 0.8,
          strokeWeight: 1,
          fillColor: zone.color,
          fillOpacity: 0.35
        })
        data.addGeoJson(coors)
        data.setMap(map)
        if (card) {
          const infowindow = new google.maps.InfoWindow({
            content: buildCard(zone),
            maxWidth: 200
          })

          data.addListener('click', () => {
            map.setCenter(zone.coordinates)
            infowindow.open(map)
            infowindow.setPosition(zone.coordinates)
          })
        }
        return data
      }
      if (zone.type === StationUserGoinZoneShapeType.SHAPE_CIRCLE) {
        const data = new google.maps.Circle({
          strokeOpacity: 0.8,
          strokeWeight: 1,
          fillColor: zone.color,
          fillOpacity: 0.35
        })
        data.setRadius(zone.radius)
        data.setCenter(zone.coordinates)
        data.setMap(map)
        if (card) {
          const infowindow = new google.maps.InfoWindow({
            content: buildCard(zone),
            maxWidth: 200
          })

          data.addListener('click', () => {
            map.setCenter(zone.coordinates)
            infowindow.open(map)
            infowindow.setPosition(zone.coordinates)
          })
        }
        return data
      }
      return null
    }

    return toMap(zone)
  }

  private refreshZones = () => {
    const me: any = this

    const zones = Object.assign({}, this.state.zones)
    for (const i of Object.keys(zones)) {
      const zone: IStationUserGoinZoneObject = zones[i]
      if (zone.mapObject) {
        zone.mapObject.setMap(null)
        zone.mapObject = null
      }
      if (!zone.visible) {
        continue
      }
      zone.mapObject = this.buildOnMap(zone, this.state.map, true)
    }
    this.setState({
      zones
    })
  }

  private getLists = () => {
    const getListByType = (type: StationUserGoinZoneType) => {
      const list: any[] = []
      for (const i of Object.keys(this.state.zones)) {
        const zone = this.state.zones[i]
        list.push(zone)
      }
      return (
        <div>
          {!!Object.keys(list).length ? (
            Object.keys(list).map((item, i) => (
              <GoinZonesListItem
                key={i}
                centerTo={this.centerTo}
                match={this.props.match}
                onEditZone={this.onEditZone}
                setZoneVisible={this.setZoneVisible}
                map={this.state.map}
                zone={list[item]}
                viewMode={this.props.viewMode}
              />
            ))
          ) : (
            <>
              {this.props.viewMode && (
                <div className='flex items-center justify-center p2'>
                  <img width={130} src='/images/no_data.gif' alt='No data availabe.' />
                </div>
              )}
            </>
          )}
        </div>
      )
    }

    return (
      <div className='su-zone-list-container'>
        {!this.props.viewMode && (
          <div className='flex items-center relative pb2'>
            <Button
              ariaLabel='Add Service Zone button'
              className='mbsc-btn-with-start-icon mbsc-success-btn'
              onClick={this.onAddServiceZoneAddButton}
              style={{ margin: '0' }}
            >
              {UIMessages.stationUser.addServiceZone}
            </Button>
            <Plus className='absolute ml2 z1' />
          </div>
        )}
        <div className='mb1'>{getListByType(StationUserGoinZoneType.SERVICE_ZONE)}</div>
      </div>
    )
  }

  private getToggleArrow = (id: string) => {
    const onToggleExpand = () => {
      const expanded = Object.assign({}, this.state.expanded)
      expanded[id] = !expanded[id]
      this.setState(
        {
          expanded
        },
        () => {
          // this.props.showMessage('', false)
        }
      )
    }
    return (
      <span>
        {!this.state.expanded[id] && (
          <span style={{ cursor: 'pointer' }} onClick={onToggleExpand}>
            <ExpandIcon className='icon--small svg-stroke-grey3 mr1' />
          </span>
        )}
        {this.state.expanded[id] && (
          <span style={{ cursor: 'pointer' }} onClick={onToggleExpand}>
            <CollapseIcon className='icon--small svg-stroke-grey3 mr1' />
          </span>
        )}
      </span>
    )
  }
}

export default withNotification(StationUserGoinZonePane)



// WEBPACK FOOTER //
// ./src/components/station-user/view/StationUserGoinZonePane.tsx